import accounting from 'accounting';
import React, { MouseEvent, useMemo } from 'react';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  PriceIndicator,
  TextBoxWithLabel,
  SpoilerAlertAppleIcon,
  CofaceLogo,
  Tooltip,
  SparkleGradientIcon,
  InformationIcon,
  MiniTooltip,
  OfferShiftOriginalIcon,
  OfferShiftReallocatedIcon,
  unitPriceCalculation,
  unitPriceDisplay,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { UnawardAndNegotiateOfferListing, createNegotiationStagedListings } from '../../../graphql/mutations';
import destinationType from '../../../enums/staged-inventory-destination-type';
import { layoutStyles } from './offer-card-styles';
import TrucklaneDetails from './trucklane-details';
import OfferCardFooter, { ActionTooltipOverride } from './offer-card-footer';
import OfferCardNegotiationsDetails from '../offer-card-negotiation-details';
import roundNumber from '../../../utilities/round';
import type { OfferCardActiveTrucklane, OfferCardUser, OfferCardOfferShift } from './offer-card-types';
import { Inventory, Listing, OfferListing } from '../../../graphql/query-result-types';
import { POSSIBLE_ACTIONS } from './offer-card-enums';

const useStyles = createUseStyles(layoutStyles, { name: 'OfferCard' });

interface OfferCardLayoutProps {
  disabled: boolean;
  offerInfo: OfferListing;
  inventory: Inventory;
  listing: Listing;
  buyerSite: {
    _id: string;
    siteName: string;
  };
  activeTruckLane: OfferCardActiveTrucklane;
  incomingOfferShift?: OfferCardOfferShift;
  user: OfferCardUser;
  showSuggestions: boolean;
  showNegotiationSuggestions: boolean;
  styles: {
    offerCardWrapper?: string;
  };
  handleAward: () => void;
  handleUnaward: () => void;
  handleIgnore: () => void;
  handleRestore: () => void;
  handleNegotiation: () => void;
  refetchQueries: (isNegotiation: boolean) => InternalRefetchQueriesInclude;
  handlePostAwardUpdateBid: (accept: boolean) => void;
  handleUnawardAndNegotiate: () => void;
  showWarningModal: boolean;
  setShowWarningModal: (show: boolean) => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
  unitPrice: number;
  setUnitPrice: (unitPrice: number) => void;
  actionInProgress: boolean;
  ignoreInProgress: boolean;
  negotiationInProgress: boolean;
  outgoingShifts?: OfferCardOfferShift[];
  handleOfferShiftViewClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  enabledActions: POSSIBLE_ACTIONS[];
  actionTooltipOverrides?: {
    [POSSIBLE_ACTIONS.AWARD]?: ActionTooltipOverride;
    [POSSIBLE_ACTIONS.IGNORE]?: ActionTooltipOverride;
    [POSSIBLE_ACTIONS.NEGOTIATE]?: ActionTooltipOverride;
  };
}

const OfferCardLayout: React.FC<OfferCardLayoutProps> = ({
  disabled,
  offerInfo,
  inventory,
  listing,
  buyerSite,
  activeTruckLane,
  user,
  showSuggestions = false,
  showNegotiationSuggestions = false,
  handlePostAwardUpdateBid,
  handleUnawardAndNegotiate,
  handleAward,
  handleUnaward,
  handleIgnore,
  handleRestore,
  handleNegotiation,
  refetchQueries,
  showWarningModal,
  setShowWarningModal,
  quantity,
  setQuantity,
  unitPrice,
  setUnitPrice,
  actionInProgress,
  ignoreInProgress,
  negotiationInProgress,
  outgoingShifts,
  incomingOfferShift,
  handleOfferShiftViewClick,
  enabledActions,
  actionTooltipOverrides = {},
  styles = {},
}) => {
  const classes = useStyles();

  const isAwarded = offerInfo.status === 'AWARDED';

  const initialQuantity = isAwarded ? offerInfo.quantity : Math.min(offerInfo.quantity, inventory.availableQuantity);

  const initialUnitPrice = unitPriceCalculation(offerInfo.totalPrice, offerInfo.quantity, offerInfo.pricingDivisor);

  const isNegotiationStaged = offerInfo?.negotiation?.status === 'STAGED';

  const isNegotiationPublished = offerInfo?.negotiation?.status === 'PUBLISHED';

  const isNegotiationReceived = offerInfo?.negotiation?.status === 'RECEIVED';

  const isIgnored = offerInfo.status === 'IGNORED';

  const isDonation = offerInfo?.destinationType === destinationType.donation;

  const isSuggested = showSuggestions && !isAwarded && offerInfo?.suggestions?.award?.suggested;

  const isSuggestedForException = showSuggestions && !isAwarded && offerInfo?.suggestions?.reservePriceException?.suggested;

  const isNegotiationSuggestion = showNegotiationSuggestions && !isAwarded && offerInfo?.suggestions?.negotiation?.recommended;

  const suggestedUnitPrice = unitPriceCalculation(
    listing.suggestedUnitPrice * listing.originalQuantity,
    listing.originalQuantity,
    offerInfo.pricingDivisor,
  );

  const reserveUnitPrice = unitPriceCalculation(
    offerInfo.activeReservePrice * offerInfo.originalQuantity,
    offerInfo.originalQuantity,
    offerInfo.pricingDivisor,
  );

  const canAward = (): boolean => {
    if (quantity > inventory.availableQuantity) return false;
    if (quantity === 0) return false;
    if (!(unitPrice > 0) && !isDonation) return false;
    if (typeof unitPrice !== 'number') return false;
    return true;
  };

  const checkAwardQuantity = () => {
    if (quantity === 0) return;
    if (quantity > listing.originalQuantity) {
      setShowWarningModal(true);
    } else {
      handleAward();
    }
  };

  const adjustQuantity = (newQuantity: string) => {
    setQuantity(accounting.unformat(newQuantity));
  };

  const adjustPricePerCase = (newPricePerCase: string) => {
    setUnitPrice(accounting.unformat(newPricePerCase));
  };

  const getLogisticsRelationshipName = () => {
    if (!offerInfo.logisticsRelationshipName) return '';
    return offerInfo.logisticsRelationshipName
      .replace('Delivery to ', '')
      .replace('Pickup for ', '')
      .replace(/^Default$/, '');
  };

  const isDisabled = disabled || (inventory.availableQuantity === 0 && !isAwarded);

  const offerShiftInfo = useMemo(() => {
    if (!isAwarded && outgoingShifts && outgoingShifts.length > 0) {
      return (
        <div className={classes.offerShiftOriginalInfo}>
          <div className={classes.offerShiftInfoContent}>
            <OfferShiftOriginalIcon className={classes.offerShiftInfoIcon} />
            Bid Shifted <span>-</span>
            <a href="#" onClick={handleOfferShiftViewClick}>
              View Shifted Bid
            </a>
          </div>
          <div className={classes.offerShiftTooltipBox}>
            <MiniTooltip
              position="left"
              text={
                <div className={classes.offerShiftTooltipText}>
                  To meet customer demand, this bid was shifted and suggested for award on similar inventory of the same item.
                </div>
              }
            >
              <div className={classes.offerShiftTooltipIconWrap}>
                <InformationIcon className={classes.offerShiftInfoIcon} />
              </div>
            </MiniTooltip>
          </div>
        </div>
      );
    }
    if (incomingOfferShift) {
      return (
        <div className={classes.offerShiftInfo}>
          <div className={classes.offerShiftInfoContent}>
            <OfferShiftReallocatedIcon className={classes.offerShiftInfoIcon} />
            Shifted Bid <span>-</span>
            <a href="#" onClick={handleOfferShiftViewClick}>
              View Original
            </a>
          </div>
          <div className={classes.offerShiftTooltipBox}>
            <MiniTooltip
              position="left"
              text={
                <div className={classes.offerShiftTooltipText}>
                  To meet customer demand, a bid on similar inventory of the same item was shifted to this inventory.
                </div>
              }
            >
              <div className={classes.offerShiftTooltipIconWrap}>
                <InformationIcon className={classes.offerShiftInfoIcon} />
              </div>
            </MiniTooltip>
          </div>
        </div>
      );
    }
    return null;
  }, [isAwarded, outgoingShifts, incomingOfferShift, handleOfferShiftViewClick, classes]);

  const wrapperClass = () => {
    let classString = styles.offerCardWrapper ? `${styles.offerCardWrapper}` : `${classes.offerCardWrapper}`;
    if (isAwarded) classString += ' awarded';
    else if (isSuggested) classString += ' suggested';
    else if (isSuggestedForException) classString += ' suggestedForException';
    else if (isNegotiationPublished) classString += ' negotiated';
    else if (isNegotiationStaged) classString += ' negotiationStaged';
    else if (isNegotiationReceived) classString += ' negotiationReceived';
    else if (isDisabled) classString += ' disabled';
    if (isIgnored) classString = `${classes.offerCardWrapper} ignored`;
    return classString;
  };

  const getAdditionalSuggestedInfo = () => {
    return isSuggested && offerInfo?.suggestions?.award ? `SUGGESTED QTY: ${accounting.formatNumber(offerInfo.suggestions.award.quantity)}` : null;
  };

  const inputQuantity = useMemo(() => {
    if (offerInfo.postAwardUpdates?.status === 'PENDING') return accounting.formatNumber(offerInfo.postAwardUpdates?.buyerUpdatedQuantity);
    if ((!inventory.availableQuantity && isAwarded) || !quantity) return accounting.formatNumber(initialQuantity);
    if (!inventory.availableQuantity) return '0';
    return accounting.formatNumber(quantity);
  }, [initialQuantity, isAwarded, offerInfo.postAwardUpdates?.buyerUpdatedQuantity, offerInfo.postAwardUpdates?.status, inventory, quantity]);

  const handleRejectPostAwardUpdateBid = () => {
    handlePostAwardUpdateBid(false);
  };

  const handleAcceptPostAwardUpdateBid = () => {
    handlePostAwardUpdateBid(true);
  };

  const ignoredClass = isIgnored ? 'ignored' : '';

  let qtyLabel = 'QTY';
  let priceLabel = 'BID';
  let pricePerCaseVal =
    unitPrice !== 0 ? unitPriceDisplay(unitPrice, offerInfo.pricingUOM) : unitPriceDisplay(initialUnitPrice, offerInfo.pricingUOM);
  let additionalInfoAwdQty: string;
  let additionalInfoAwdBid: string;
  let displayPostAwardUpdatesBanner = false;

  if (isNegotiationSuggestion && offerInfo?.suggestions?.negotiation) {
    additionalInfoAwdQty = `SUGGESTED QTY: ${accounting.formatNumber(offerInfo?.suggestions?.negotiation?.quantity)}`;
    additionalInfoAwdBid = `SUGGESTED PRICE: ${accounting.formatMoney(offerInfo?.suggestions?.negotiation?.unitPrice)}`;
  }

  if (offerInfo?.postAwardUpdates?.status === 'PENDING' && offerInfo?.postAwardUpdates) {
    displayPostAwardUpdatesBanner = true;
    const { awardedQuantity, awardedTotalPrice } = offerInfo.postAwardUpdates;
    additionalInfoAwdQty = `AWARDED QTY: ${accounting.formatNumber(awardedQuantity)}`;
    additionalInfoAwdBid = `AWARDED BID: ${accounting.formatMoney(awardedTotalPrice / awardedQuantity)}`;
    priceLabel = 'UPDATED BID';
    qtyLabel = 'UPDATED QTY';
    pricePerCaseVal = accounting.formatMoney(offerInfo?.postAwardUpdates?.buyerUpdatedTotalPrice / offerInfo?.postAwardUpdates?.buyerUpdatedQuantity);
  }

  const renderBanner = () => (
    <div className={`${classes.postAwardUpdatesBannerOuter}`}>
      <div className={`${classes.postAwardUpdatesBannerInner}`}>
        The customer updated their offer post-award. Review the changes below to approve or reject.
      </div>
    </div>
  );

  const renderInputs = () => (
    <div className={`${classes.offerCardEditable} ${ignoredClass}`}>
      <TextBoxWithLabel
        isValid={(value: string) => {
          const number = accounting.unformat(value);
          return number <= inventory.availableQuantity && number > 0;
        }}
        onChange={(newQuantity: string) => adjustQuantity(newQuantity)}
        disabled={isAwarded || isDisabled || isIgnored || !enabledActions.includes(POSSIBLE_ACTIONS.EDIT_QTY)}
        label={qtyLabel}
        value={inputQuantity}
        denominator={accounting.formatNumber(listing.originalQuantity)}
        className={isDonation ? classes.donationTextboxWithDenominator : classes.textboxWithDenominatorleft}
        additionalInfo={additionalInfoAwdQty}
        additionalInfoTop={getAdditionalSuggestedInfo()}
        additionalInfoTopClassName={classes.textboxWithNumerator}
        additionalInfoClassName={classes.textboxWithDenominatorleft}
        additionalLabelClassName={displayPostAwardUpdatesBanner ? classes.offerCardFooterLabelPostAward : null}
        id={'QuantityTextBox'}
        postAwardUpdates={displayPostAwardUpdatesBanner}
      />
      {!isDonation && (
        <TextBoxWithLabel
          isValid={(value: string) => accounting.unformat(value) > 0}
          onChange={(newPricePerCase: string) => adjustPricePerCase(newPricePerCase)}
          disabled={isAwarded || isDisabled || isIgnored || !enabledActions.includes(POSSIBLE_ACTIONS.EDIT_PRICE)}
          additionalInfo={additionalInfoAwdBid}
          label={priceLabel}
          value={pricePerCaseVal}
          additionalLabelClassName={displayPostAwardUpdatesBanner ? classes.offerCardFooterLabelPostAward : null}
          id={'PriceTextBox'}
          postAwardUpdates={displayPostAwardUpdatesBanner}
          pricingUOM={offerInfo.pricingUOM}
        />
      )}
    </div>
  );

  const renderNegotiationReceivedText = useMemo(() => {
    const unitPrice = roundNumber(offerInfo?.negotiation?.buyerResponseUnitPrice || offerInfo.totalPrice / offerInfo.quantity || 0, 2);
    const stagedUnitPrice = roundNumber(offerInfo?.negotiation?.stagedUnitPrice || 0, 2);
    const originalUnitPrice = roundNumber(offerInfo.originalTotalPrice / offerInfo.originalQuantity, 2);
    if (unitPrice > stagedUnitPrice) {
      return 'COUNTER EXCEEDED';
    }
    if (unitPrice === stagedUnitPrice) {
      return 'COUNTER MET';
    }
    if (unitPrice > originalUnitPrice) {
      return 'PARTIAL INCREASE';
    }
    if (unitPrice < originalUnitPrice) {
      return 'REDUCED BID';
    }
    return 'NO CHANGE';
  }, [
    offerInfo?.negotiation?.buyerResponseUnitPrice,
    offerInfo?.negotiation?.stagedUnitPrice,
    offerInfo.originalQuantity,
    offerInfo.originalTotalPrice,
    offerInfo.quantity,
    offerInfo.totalPrice,
  ]);

  return (
    <div className={wrapperClass()} data-cy="offer-card-wrapper" data-testid="offer-card">
      <div className={classes.innerWrap}>
        {/* This is all just the header bar */}
        {!isAwarded && isNegotiationStaged && !isDonation && !isIgnored && (
          <div className={`${classes.offerCardNegotiationTag} ${classes.offerCardNegotiationTagDark}`}>Negotiation Staged</div>
        )}
        {!isAwarded && isNegotiationPublished && !isIgnored && <div className={classes.offerCardNegotiationTag}>Negotiation Published</div>}
        {!isAwarded && isNegotiationReceived && !isIgnored && (
          <div className={`${classes.offerCardNegotiationTag} ${classes.offerCardNegotiationTagBlue}`}>
            Negotiation Received - {renderNegotiationReceivedText}
          </div>
        )}
        {isAwarded && <div className={classes.offerCardAwardedTag}>Awarded</div>}
        {isIgnored && <div className={classes.offerCardIgnoredTag}>Ignored</div>}

        {offerShiftInfo}
        <div className={`${classes.offerCardHeader} ${ignoredClass}`}>
          <div className={classes.offerCardTitles}>
            {!offerInfo.isVendorOfRecord && <h4 className={classes.buyerName}>{`${isDonation ? 'Donation: ' : ''}${buyerSite.siteName}`}</h4>}
            {offerInfo.isVendorOfRecord && (
              <Tooltip
                className={classes.tooltipWrapper}
                text={
                  <div className={classes.tooltipContainer}>
                    <span>This is a Spoiler Alert buyer with insured payments through Coface For Trade.</span>
                    <CofaceLogo className={classes.cofaceLogo} />
                  </div>
                }
              >
                <div className={classes.buyerNameContainer}>
                  <h4 className={classes.buyerName}>{`${isDonation ? 'Donation: ' : ''}${buyerSite.siteName}`}</h4>
                  <SpoilerAlertAppleIcon className={classes.spoilerAlertApple} />
                </div>
              </Tooltip>
            )}
            {getLogisticsRelationshipName() !== '' && <h5>{getLogisticsRelationshipName()}</h5>}
          </div>
          {isSuggested && <SparkleGradientIcon className={classes.sparkleIcon} />}
        </div>
        {displayPostAwardUpdatesBanner && renderBanner()}
        {renderInputs()}
        <div style={{ padding: '0px 16px 19px' }}>
          {/* On donations theres no price indicator */}
          {!isDonation && (
            <PriceIndicator
              suggestedPrice={suggestedUnitPrice}
              reservePrice={reserveUnitPrice}
              pricePerCase={unitPrice || initialUnitPrice}
              pricingUOM={offerInfo.pricingUOM}
              tooltipText={
                <div>
                  <div>{`${accounting.formatNumber((unitPrice / suggestedUnitPrice) * 100, 0)}% of suggested price`}</div>
                  {offerInfo.activeReservePrice > 0 && (
                    <div>{`${accounting.formatNumber((unitPrice / reserveUnitPrice) * 100, 0)}% of reserve price`}</div>
                  )}
                </div>
              }
            />
          )}
        </div>
        {(isNegotiationStaged || isNegotiationPublished || isNegotiationReceived) && (
          <OfferCardNegotiationsDetails
            offerInfo={offerInfo}
            user={user}
            isNegotiationReceived={isNegotiationReceived}
            chevronArrowDirection={isAwarded || isIgnored ? 'down' : 'up'}
          />
        )}
        <TrucklaneDetails
          activeTruckLane={activeTruckLane}
          offerInfo={offerInfo}
          inventory={inventory}
          isAwarded={isAwarded}
          quantity={quantity}
          user={user}
          unitPrice={unitPrice}
        />
        <OfferCardFooter
          handleUnaward={handleUnaward}
          handleUnawardAndNegotiate={handleUnawardAndNegotiate}
          checkAwardQuantity={checkAwardQuantity}
          ignoredClass={ignoredClass}
          offerInfo={offerInfo}
          inventory={inventory}
          listing={listing}
          buyerSite={buyerSite}
          quantity={quantity}
          pricePerCase={unitPrice}
          isAwarded={isAwarded}
          isDonation={isDonation}
          isIgnored={isIgnored}
          actionInProgress={actionInProgress}
          ignoreInProgress={ignoreInProgress}
          negotiationInProgress={negotiationInProgress}
          handleRestore={handleRestore}
          handleIgnore={handleIgnore}
          canAward={canAward}
          disabled={disabled}
          refetchQueries={refetchQueries}
          UnawardAndNegotiateOfferListing={UnawardAndNegotiateOfferListing}
          createNegotiationStagedListings={createNegotiationStagedListings}
          handleNegotiation={handleNegotiation}
          handleAcceptPostAwardUpdateBid={handleAcceptPostAwardUpdateBid}
          handleRejectPostAwardUpdateBid={handleRejectPostAwardUpdateBid}
          user={user}
          enabledActions={enabledActions}
          actionTooltipOverrides={actionTooltipOverrides}
        />
        <Modal onHide={() => setShowWarningModal(false)} open={showWarningModal} closeOnEsc closeOnOutsideClick modalWidth={500}>
          <ModalContent>
            <h2>Warning</h2>
            <p>The amount you are trying to award is greater than the amount listed. Do you want to award anyway?</p>
          </ModalContent>
          <ModalFooter>
            <Button type="button" onClick={handleAward}>
              Award
            </Button>
            <Button type="button" onClick={() => setShowWarningModal(false)} link>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default OfferCardLayout;
